import React from "react";

import UIButton from "compid/ui/UIButton";
import { SVGAsset } from "res";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "../..";

import UITextInput from "compid/ui/UITextInput";

import AccordionView, { AccordionContent, AccordionHeader } from "compid/shared/views/AccordionView";
import { Box, Stack } from "@mui/material";

import "./styles.css";
/**
 * @function BillerGroupsAccordion
 * @param {STRING} className - Allows us to set a unique className.
 * @param {STRING} text - Sets the text of the CustomAccordionToggle.
 * @param {React.Component} children - Sets React components inside the CustomAccordionContent.
 * @returns {React.Component}
 * @exported true
 * @description This is a variation of the CustomAccordion used only for biller groups.
 * @example
 * <BillerGroupsAccordion className="my-accordion" text="Biller Group Name">
 *   <p>This is my content.</p>
 * </BillerGroupsAccordion>
 */
export default function BillerGroupsAccordion(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[billerGroupName]";  
  const dataPoint = props.dataPoint ?? undefined;
  const billerGroup = props.billerGroup ?? {};
  const billerGroupEditItems = props.billerGroupEditItems ?? [];
  const chevronPoint = dataPoint ? { "data-point": dataPoint + "_Chevron" } : {};
  const textPoint = dataPoint ? { "data-point": dataPoint + "_Name" } : {};
  const billerGroupEditItem = billerGroupEditItems.filter(item => item.billerGroup.id === billerGroup.id)[0];
  const isEditing = billerGroupEditItems.filter(item => item.billerGroup.id === billerGroup.id).length > 0 ? true : false;
  const editedBillerGroup = billerGroupEditItem ? billerGroupEditItem.editedBillerGroup : {};

  const onChange = prop => val => {
    props.updateBillerGroupEditItem({ billerGroupEditItem: billerGroupEditItem, prop: prop, val: val });
  }

  return (
    <AccordionView className={"biller-groups-accordion flex-height " + { className }} defaultExpanded={ true }>
      <AccordionHeader className="biller-groups-accordion-toggle" >
        <Stack direction="row" alignItems="center"  >
          <SVGAsset id="DownChevronIcon" className="chevron-icon left-icon set-width" { ...chevronPoint } />
          { !isEditing &&
          <span className="flex-width" { ...textPoint }>{ text }</span>
          }
          { isEditing &&
          <Box onClick={(evt)=>{evt.stopPropagation()}}>
            <UITextInput className="biller-groups" { ...textPoint } value={ editedBillerGroup.name } ignoreToggle={ true } onChange={ onChange("name") } />
          </Box>
          }
         </Stack>
      </AccordionHeader>
      <AccordionContent className="biller-groups-accordion-content flex-container flex-vertical">
        {props.children}
      </AccordionContent>
    </AccordionView>
  );
}

/**
 * @function BillerGroupsButtonContainer
 * @param {STRING} className - Sets a unique className for the component.
 * @exported true
 * @returns {React.Component}
 * @description - This is a container component that wraps any buttons or any other UI components in the BillerGroupsAccordion.
 * @example
 * <BillerGroupsAccordion className="my-accordion" text="Biller Group Name">
 *   <BillerGroupsButtonContainer className="my-button-container">
 *      <BillerGroupsButton text="text" icon="IconID" className="button-class" />
 *   </BillerGroupsButtonContainer>
 * </BillerGroupsAccordion>
 */
export function BillerGroupsButtonContainer(props) {
  const className = props.className ?? "";

  return(
    <div className={"biller-groups-button-container flex-container flex-horizontal align-center " + className}>
      {props.children}
    </div>
  );
}

/**
 * @function BillerGroupsButton
 * @param {STRING} className - Sets a unique className for the component.
 * @param {STRING} text - Sets the text of the button.
 * @param {STRING} icon - Sets the icon of the button.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @exported true
 * @returns {React.Component}
 * @description - This is a variation of the UIButton that is used only in the BillerGroupsAccordion.
 * @example
 * <BillerGroupsAccordion className="my-accordion" text="Biller Group Name">
 *   <BillerGroupsButtonContainer className="my-button-container">
 *      <BillerGroupsButton text="text" icon="IconID" className="button-class" />
 *   </BillerGroupsButtonContainer>
 * </BillerGroupsAccordion>
*/
export function BillerGroupsButton(props) {
  const icon = props.icon !== undefined ? (<SVGAsset id={props.icon} className="set-width" />) : (<></>);
  const text = props.text ?? "[button text]";
  const className = props.className ?? "";

  return(
    <UIButton className={ "biller-groups-button " + className } onClick={props.onClick} dataPoint={ props.dataPoint }>
      {icon}
      <span className="biller-groups-button-text sub-text">{ text }</span>
    </UIButton>
  );
}